import { useMemo } from "react";

import {
  formatMaterialDimensions,
  FormatterMaterialParameter,
  getMaterialDimensionsFormatExplanation,
  MaterialGradeType,
  MaterialLayout,
  PartTypeSchema,
  PartTypeType,
} from "shared";

import { useDimensionOptions } from "./useDimensionOptions";
import {
  SHOW_OTHER_DIMENSIONS_OPTION,
  useExpandableDimensionOptions,
} from "./useExpandableDimensionOptions";
import { useUpdateLayoutMaterial } from "./useUpdateLayoutMaterial";

const CUSTOM_DIMENSION_SELECT_OPTION = "CUSTOM_DIMENSION";

type Params = {
  materialLayout: MaterialLayout;
  materialGrade: MaterialGradeType;
  materialName: string;
  quoteRequestLineId?: string;
  partType: PartTypeType | null;
  partSubType: string | null;
  partDimensionsWithOverthickness: FormatterMaterialParameter;
  controlled?: {
    value: string | null;
    onChange: (newMaterialId: string) => void;
  };
};

export const useMaterialDimensionsSelect = ({
  materialLayout,
  materialGrade,
  materialName,
  quoteRequestLineId,
  partType,
  partSubType,
  partDimensionsWithOverthickness,
  controlled,
}: Params) => {
  const { updateLayoutMaterial } = useUpdateLayoutMaterial({
    materialLayout,
    materialGrade,
    quoteRequestLineId,
  });
  const materialLayoutType = materialLayout.type || partType || PartTypeSchema.enum.OTHER;

  const dimensionExplanation = getMaterialDimensionsFormatExplanation(
    materialLayoutType,
    materialLayout,
  );

  const selectId = `qrl-dimensions-${materialLayout.id}`;

  const controlledValue = controlled?.value;

  const { recommendedOptions, otherOptions } = useDimensionOptions({
    materialGrade,
    materialName,
    partSubType,
    partType,
    partDimensionsWithOverthickness,
  });

  const {
    hasCustomDimensions,
    selectedMaterialId,
    recommendedOptionsWithCustom,
    isSelectedDimensionInOtherOptions,
  } = useMemo(() => {
    const allOptions = [...recommendedOptions, ...otherOptions];
    const selectedOption =
      allOptions.find((option) => option.value === controlledValue) ??
      allOptions.find(
        (option) => formatMaterialDimensions(materialLayoutType, materialLayout) === option.label,
      );

    if (selectedOption) {
      return {
        recommendedOptionsWithCustom: recommendedOptions,
        hasCustomDimensions: false,
        selectedMaterialId: selectedOption.value,
        isSelectedDimensionInOtherOptions: otherOptions.includes(selectedOption),
      };
    }

    return {
      isSelectedDimensionInOtherOptions: false,
      recommendedOptionsWithCustom: [
        ...recommendedOptions,
        {
          value: CUSTOM_DIMENSION_SELECT_OPTION,
          label: formatMaterialDimensions(materialLayoutType, materialLayout),
        },
      ],
      hasCustomDimensions: true,
      selectedMaterialId: undefined,
    };
  }, [recommendedOptions, otherOptions, materialLayoutType, materialLayout, controlledValue]);

  const { dimensionGroups, expandableSelectProps } = useExpandableDimensionOptions({
    recommendedOptions: recommendedOptionsWithCustom,
    otherOptions,
    forceShowOtherOptions: isSelectedDimensionInOtherOptions,
  });

  const onChange = (newMaterialId: string | null) => {
    if (!newMaterialId || newMaterialId === SHOW_OTHER_DIMENSIONS_OPTION) {
      return;
    }

    if (controlled) {
      controlled.onChange(newMaterialId);
      return;
    }

    updateLayoutMaterial(newMaterialId);
  };

  const currentValue = hasCustomDimensions
    ? CUSTOM_DIMENSION_SELECT_OPTION
    : selectedMaterialId || null;
  const value = controlled?.value ?? currentValue;

  return {
    selectId,
    dimensionExplanation,
    allOptions: [...recommendedOptionsWithCustom, ...otherOptions],
    dimensionGroups,
    expandableSelectProps,
    value,
    onChange,
  };
};
